import { isNumber } from 'lodash';
import { ApartmentEntry, PolygonType, ProjectPolygonPriceBy, TProject } from '../../types/config';
import { formatPrice, cookie } from '../../utils';
import { InventoryRow, InventoryRowColValue } from './types';

const inventoryRowDefaults = {
  name: '',
  [InventoryRowColValue.Rooms]: 0,
  roomsOutput: '-',
  [InventoryRowColValue.LivingAreas]: 0,
  livingAreasOutput: '-',
  [InventoryRowColValue.Floors]: 0,
  floorsOutput: '-',
  [InventoryRowColValue.Prices]: 0,
  pricesOutput: '-',
};

export function getInventoryConfig(
  t: (key: string, options?: any) => string,
  project: TProject,
  aptArray?: ApartmentEntry[],
): [InventoryRow[], boolean, boolean, boolean, boolean] {
  let withRooms = false;
  let withLivingAreas = false;
  let withFloors = false;
  let withPrices = false;
  const rowsData: InventoryRow[] = project.polygons.map((data) => {
    const aptEntry = aptArray?.find((apt) => data.tourUrl && apt.tourUrl === data.tourUrl);
    const blueprintAsset = project.assets.find((a) => a.polygonId === data.id);

    // Check if interactive features should be disabled based on grade cookie
    const shouldDisableTours = cookie.shouldDisableInteractiveFeatures(project?.yzerId);

    const row: InventoryRow = {
      ...inventoryRowDefaults,
      title: data.name || t(`labels.${data.type}`),
      type: data.type,
      polygonId: data.id,
      nrId: aptEntry?.nrId || data.id || 0,
      tourUrl: shouldDisableTours ? undefined : aptEntry?.tourUrl || data.tourUrl,
      tourPreviewUrl: aptEntry?.mainImage || data?.blueprintAsset?.preview || blueprintAsset?.url,
      order: data.order || 0,
    };
    if (!!data.rooms) {
      row[InventoryRowColValue.Rooms] = Math.abs(data.rooms);
      row.roomsOutput = Math.abs(data.rooms);
      withRooms = true;
    }
    if (!!data.livingArea) {
      row[InventoryRowColValue.LivingAreas] = Math.abs(data.livingArea);
      row.livingAreasOutput = t('labels.m2', { v: Math.abs(data.livingArea) });
      withLivingAreas = true;
    }
    if (isNumber(data.minFloor) || isNumber(data.maxFloor)) {
      const floorsAbs = [Math.abs(data.minFloor), Math.abs(data.maxFloor)];
      const floorsMin = Math.min(...floorsAbs);
      const floorsMax = Math.max(...floorsAbs);
      row[InventoryRowColValue.Floors] = floorsMin;
      // floorsMin can be 0
      if (!!floorsMax) {
        if (floorsMin !== floorsMax) {
          row.floorsOutput = floorsMin ? `${floorsMin}-${floorsMax}` : floorsMax;
        }
        if (floorsMin === floorsMax) {
          row.floorsOutput = `${floorsMin}`;
        }
        withFloors = true;
      }
      if (data.type === PolygonType.Patio) {
        row.floorsOutput = t('labels.ground');
        withFloors = true;
      }
    }
    if (data.showPrice && Number(data.price)) {
      row[InventoryRowColValue.Prices] = Math.abs(data.price);
      row.pricesOutput = t(
        data.priceBy === ProjectPolygonPriceBy.Total
          ? 'labels.startingFromMoney'
          : 'labels.startingFromPerSquareMeters',
        { v: formatPrice(Math.abs(data.price)) },
      );
      withPrices = true;
    }
    return row;
  });

  return [rowsData, withRooms, withLivingAreas, withFloors, withPrices];
}
